import type { ReactElement } from 'react'
import styled from 'styled-components'

import { theme } from '@/styles/theme'

import { SectionTitle } from '@/components/elements/SectionTitle'
import { Spacer } from '@/components/elements/Spacer'
import { TitleLayout } from '@/components/layout'
import { Head } from '@/components/layouts/Head'
import { Layout } from '@/components/layouts/Ir'
import { BulletedList } from '@/components/layouts/Ir/BulletedList'
import { AnotationText } from '@/components/layouts/Ir/Mpocket/AnotationText'
import { Paragraph } from '@/components/layouts/Ir/Paragraph'

import { useMpocketScript } from '@/hooks/useMpocketScript'

const chartList = [
  { title: '売上高', id: 'irp-chart-graph-uriage' },
  { title: '営業利益', id: 'irp-chart-graph-eigyo' },
  { title: '経常利益', id: 'irp-chart-graph-keijo' },
  { title: '親会社株主に帰属する当期純利益', id: 'irp-chart-graph-oya' },
  { title: '総資産', id: 'irp-chart-graph-soshisan' },
  { title: '純資産', id: 'irp-chart-graph-junshisan' },
  { title: '自己資本利益率(ROE)', id: 'irp-chart-graph-roe' },
  { title: '自己資本比率', id: 'irp-chart-graph-jiko' },
  { title: '店舗数', id: 'irp-chart-graph-tenpo' },
]

export const ReviewPage = () => {
  useMpocketScript('//irpocket.com/141A/irpocket/ir-financial-review.js')
  // useMpocketScript('//irpocket.com/5882/irpocket/ir-financial-review.js')
  return (
    <Layout>
      <link rel="stylesheet" href="//irpocket.com/141A/irpocket/irp-style.css"></link>
      <SectionTitle
        titleText="Financial Highlights"
        subTitleText="決算レビュー"
        color="normal"
      />
      <Spacer h={{ sp: 70, pc: 120 }} />
      <AnotationText id="irp-highlight-table-review1" />
      <Spacer h={{ sp: 30, pc: 50 }} />
      <BulletedList>
        {/* <li>
          当社は、2021年2月1日開催の臨時株主総会の決議により決算期末を3月20日から6月30日に変更しております。そのため、参考情報として、2020年7月1日から2021年6月30日までの12ヵ月を一連結会計年度と仮定して計算した数値（未監査）を2021年6月期（LTM）として記載しております。
        </li> */}
        <li>売上高及び利益は、四半期ごとの累計を表示しております。</li>
      </BulletedList>
      <Spacer h={{ sp: 70, pc: 100 }} />
      <ChartWrapper>
        {chartList.map(({ title, id }) => (
          <ChartContainer key={id}>
            <Title>{title}</Title>
            <Spacer h={{ sp: 20, pc: 36 }} />
            <Chart id={id} />
          </ChartContainer>
        ))}
      </ChartWrapper>
      <Spacer h={{ sp: 80, pc: 100 }} />
      <AnotationText id="irp-highlight-table-review2" />
      <Paragraph>
        グラフ下部の凡例をクリックすると、グラフ表示のON/OFFを切り替えることができます。また、グラフにカーソルを合わせると、対象期間と数値が表示されます。
      </Paragraph>
    </Layout>
  )
}

ReviewPage.getLayout = (page: ReactElement) => {
  const breadCrumbs = [
    { name: 'ホーム', path: '/' },
    { name: '株主・投資家向け情報', path: '/ir' },
    { name: '業績・財務情報', path: '/ir/financial/' },
    { name: '決算レビュー', path: '' },
  ]

  return (
    <>
      <Head
        title="決算レビュー"
        description="トライアルホールディングスの四半期ごとの決算概要について掲載しています。"
        ogp={{ url: 'ir/financial/review' }}
      />
      <TitleLayout
        title="Investor Relations"
        subTitle="株主・投資家向け情報"
        breadCrumbs={breadCrumbs}
      >
        {page}
      </TitleLayout>
    </>
  )
}

const ChartWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 80px;

  @media screen and (max-width: 1047px) {
    grid-template-columns: repeat(1, 1fr);
    gap: 50px;
  }
`

const ChartContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const Title = styled.h3`
  font-size: 24px;
  font-weight: bold;
  color: ${theme.colors.trialBlue};
  line-height: 33.6px;

  @media screen and (max-width: 1047px) {
    font-size: 18px;
    line-height: 23.4px;
  }
`

const Chart = styled.div`
  height: 293px;
  width: 470px;

  @media screen and (max-width: 1047px) {
    height: 185.32px;
    width: 100%;
  }
`
